<template>
    <div class="h-full">
        <div class="flex flex-col justify-between rounded-4 border p-24 h-564 overflow-hidden">
            <div>
                <tag
                    v-if="product.free_board"
                    display="secondary"
                    class="w-full mb-24"
                >
                    <span class="w-full text-center">{{ jobBoardStatus }}</span>
                </tag>
                <div class="flex justify-between mb-8">
                    <p>{{ `${product.duration.period} ${product.duration.range}` }}</p>
                    <p>{{ price }}</p>
                </div>
                <div class="flex items-center">
                    <svg-icon type="schedule" class="w-16 h-16 mr-8" />
                    <span class="lowercase">{{ deliverTime }}</span>
                </div>
            </div>
            <div class="flex justify-center items-center w-full">
                <img :src="productLogo" :alt="`logo-${product.title}`" :class="[logoWidth, logoHeight]">
            </div>
            <div class="my-16">
                <p class="text-body font-semibold my-8">{{ product.name }}</p>
                <a :href="product.url" target="_blank" class="hover:underline break-words link-default">{{ product.url }}</a>
            </div>
            <div class="flex flex-col items-center w-full">
                <simple-button
                    display="secondary"
                    class="w-full"
                    look="primary"
                    @click="addToCampaign"
                >
                    {{ $t('multi-posting.contracts.index.add_to_campaign') }}
                </simple-button>

                <simple-button
                    class="w-full my-8"
                    @click="viewDetails"
                >
                    {{ $t('multi-posting.contracts.index.details') }}
                </simple-button>

                <simple-button
                    v-if="vacancyCount > 0"
                    look="text"
                    @click="viewVacancies"
                >
                    {{ `${$t('multi-posting.job-boards.view_vacancies')} (${vacancyCount})` }}
                </simple-button>
            </div>
        </div>
    </div>
</template>

<script>
import SimpleButton from "../ui/SimpleButton.vue";
import SelectDropdown from "../forms/SelectDropdown.vue";
import MultiPostingDynamicContractForm from "./MultiPostingDynamicContractForm.vue";
import SvgIcon from "../ui/SvgIcon.vue";
import {mapState} from "pinia";
import {useMultiPostingStore} from '../../state/modules/multiPosting';
import Tag from "../ui/Tag.vue";

export default {
    name: 'JobBoardListItem',
    components: {
        Tag,
        MultiPostingDynamicContractForm, SelectDropdown, SimpleButton,
        SvgIcon,
    },
    props: {
        product: { type: Object, required: true },
    },
    computed: {
        ...mapState(useMultiPostingStore, ['productJobData']),
        price() {
            if (this.product.vonq_price[0].amount === 0) {
                return this.$t('multi-posting.price.free')
            } else {
                return `${this.product.vonq_price[0].amount.toFixed(2)} ${this.product.vonq_price[0].currency}`;
            }
        },
        deliverTime() {
            // always in hours
            const duration = this.product.time_to_process + this.product.time_to_setup;
            if (duration < 24)
                return `${duration} ${this.$tc('general.hours', duration)}`;
            const days = Math.floor(duration / 24);
            return `${days} ${this.$tc('general.working_days', days)}`;
        },
        logoWidth() {
            const measurements = [64, 128, 240, 320];
            let width = 0;

            if (this.product.logo_rectangle_size) {
                width = this.product.logo_rectangle_size.width;
            } else if (this.product.logo_square_size) {
                width = this.product.logo_square_size.width;
            }

            return `max-w-${this.getClosestNumber(measurements, width)}`;
        },
        logoHeight() {
            const measurements = [64, 128, 240, 320];
            let height = 0;

            if (this.product.logo_rectangle_size) {
                height = this.product.logo_rectangle_size.height;
            } else if (this.product.logo_square_size) {
                height = this.product.logo_square_size.height;
            }

            return `max-h-${this.getClosestNumber(measurements, height)}`;
        },
        productLogo() {
            return this.product.logo_rectangle_url;
        },
        jobBoardStatus() {
            return this.product.free_board ? this.$t('multi-posting.campaigns.form.free_board') : this.$t('multi-posting.jobboards.locked');
        },
        jobBoardHeaderColor() {
            return this.product.free_board ? 'bg-secondary' : 'bg-primary';
        },
        vacancyCount() {
            return this.productJobData.filter(x => x.product_id === this.product.id).length;
        }
    },
    methods: {
        getClosestNumber(arr, number) {
            if (!number) number = 0;
            return arr.reduce(function(prev, curr) {
                return (Math.abs(curr - number) < Math.abs(prev - number) ? curr : prev);
            });
        },
        addToCampaign() {
            this.$emit('add-to-campaign');
        },
        viewDetails() {
            this.$emit('view-details', this.product);
        },
        viewVacancies() {
            this.$emit('view-vacancies', this.product);
        }
    },
}
</script>
